import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { PhxPageChangeEvent } from '../phx-table.types';
import { Bullet16, ChevronLeft, ChevronRight, ChevronUpDown } from '../../icon/icon-map';
import { PhxSelectOption } from '../../select/select.types';
import { PhxBreakpointObserverService } from '../../../services/phx-breakpoint-service';
import { KeyboardKeys } from '../../../helpers/KeyboardKeys.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'phx-table-pagination',
  templateUrl: './phx-table-pagination.component.html',
  styleUrls: ['./phx-table-pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-table-pagination',
  },
})
export class PhxTablePaginationComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Total number of items across all pages. When omitted, total-count labels are hidden. */
  @Input() totalItems: number | undefined;
  /** Whether another page exists when `totalItems` is omitted. */
  @Input() hasNextPage: boolean | undefined;
  /** Number of items shown per page. Default `25`. */
  @Input() perPage: number = 25;
  /** Current 1-based page index. Default `1`. */
  @Input() currentPage: number = 1;
  /** Options shown in the rows-per-page selector. */
  @Input() perPageOptions: number[] | undefined;
  /** Formatted absolute last-updated timestamp (date, time, timezone). */
  @Input() lastUpdatedAtLabel = '';
  /** Formatted relative last-updated label (e.g. "24m ago"). */
  @Input() lastUpdatedAtRelativeLabel = '';

  /** Emitted when the user navigates to a different page or changes rows-per-page. */
  @Output() pageChange = new EventEmitter<PhxPageChangeEvent>();

  public readonly prevIcon = ChevronLeft;
  public readonly nextIcon = ChevronRight;
  public readonly pageSizeChangeIcon = ChevronUpDown;
  public readonly bulletIcon = Bullet16;

  public isTabletOrMobileView = false;
  public selectOptions: PhxSelectOption[] = [];
  /** Number of pages. Only meaningful when `totalItems` is provided — see `hasTotalItems`. */
  public totalPages: number = 1;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private _phxBreakpointObserver: PhxBreakpointObserverService,
    private _cdr: ChangeDetectorRef,
  ) {}

  public ngAfterViewInit(): void {
    this._phxBreakpointObserver.mdTablet.pipe(takeUntil(this._destroy$)).subscribe((value: boolean) => {
      this.isTabletOrMobileView = value;
      this._cdr.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public get hasTotalItems(): boolean {
    return this.totalItems != null;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { totalItems, perPage, currentPage, perPageOptions } = changes;

    if (totalItems || perPage || currentPage) {
      this.totalPages = this.hasTotalItems ? Math.max(1, Math.ceil(this.totalItems! / this.perPage)) : 1;
    }

    if (perPageOptions) {
      this.selectOptions = (this.perPageOptions || []).map(n => ({ label: String(n), value: n }));
    }
  }

  public get isPrevDisabled(): boolean {
    return this.currentPage <= 1;
  }

  public get isNextDisabled(): boolean {
    if (!this.hasTotalItems) {
      return this.hasNextPage === false;
    }

    return this.currentPage >= this.totalPages;
  }

  public getCurrentPageCount(): number {
    if (!this.hasTotalItems || this.currentPage < this.totalPages) {
      return this.perPage;
    }

    const remainder = this.totalPages * this.perPage - this.totalItems!;
    return remainder > 0 ? this.totalItems! % this.perPage : this.perPage;
  }

  public onPrev(): void {
    if (this.isPrevDisabled) return;
    this.pageChange.emit({ page: this.currentPage - 1, perPage: this.perPage });
  }

  public onNext(): void {
    if (this.isNextDisabled) return;
    this.pageChange.emit({ page: this.currentPage + 1, perPage: this.perPage });
  }

  public onPerPageChange(option: PhxSelectOption | PhxSelectOption[]): void {
    const selected = Array.isArray(option) ? option[0] : option;
    const newPerPage = selected.value as number;
    if (newPerPage === this.perPage) return;
    this.pageChange.emit({ page: 1, perPage: newPerPage });
  }

  public onKeydown(event: KeyboardEvent, action: () => void): void {
    if (event.code === KeyboardKeys.ENTER || event.code === KeyboardKeys.SPACE) {
      event.preventDefault();
      action();
    }
  }
}
